import _get from 'lodash/get';
import _find from 'lodash/find';
import _findKey from 'lodash/findKey';
import _each from 'lodash/each';
import _isEmpty from 'lodash/isEmpty';
import { getDonateButtonImageUrl } from '../utilsV3/buttonImage';
import {
  getSupportedVariables,
  getUnsupportedVariables,
  formatVariablesForTextArea,
  parseTextAreaValue,
} from './extraVariables';
import _includes from 'lodash/includes';
import { isLoggedInUser } from '../utilsV3/authUtils';
import { normalizeNumber } from '../utilsV3/amountUtils';
import {
  LOGO_TARGET_NOLOGO,
  LOGO_TARGET_BUTTON,
  LOGO_TARGET_PROFILE,
} from '../reducers/reducerButtonFactoryV3';
import isEmpty from 'lodash/isEmpty';
import templateData from '../../utility/getTemplateData';
import { getServerProps } from '../utilsV3/serverProps';
import { formatNumber } from '../../utility/worldReadyUtils';

export const ACCOUNT_IDENTIFIER_TYPES = {
  MERCHANT_ACCOUNT_ID: 'merchantAccountId',
  EMAIL_ADDRESS: 'emailAddress',
};

// All supported server button props
export const SERVER_BUTTON = {
  AMOUNT_OPTIONS: 'amount_options',
  AMOUNT_TYPE: 'amount_type',
  AMOUNT: 'amount',
  BN: 'bn',
  BTN_CODE_TYPE: 'btn_code_type',
  BUSINESS: 'business',
  BUTTON_IMAGE_URL: 'button_image_url',
  BUTTON_IMAGE: 'button_image',
  BUTTON_NAME: 'button_name',
  BUTTON_VARIABLES: 'button_variables',
  CANCEL_RETURN: 'cancel_return',
  CN: 'cn',
  CODE: 'code',
  COUNTRY_CODE: 'country_code',
  COVER_FEES_OPTION: 'donor_covers_fees',
  CURRENCY_CODE: 'currency_code',
  DONATE_CONTEXT: 'donate_context',
  RECURRING_EXTN_DATA: 'recurring_extn_data',
  EMAIL_CODE: 'email_code',
  FLOW_TYPE: 'int_type',
  ID: 'id',
  IMAGE_BANNER: 'banner_url',
  ITEM_NAME: 'item_name',
  ITEM_NUMBER: 'item_number',
  LANGUAGE: 'language',
  LC: 'lc',
  LOGO_TARGET: 'logo_target',
  LOGO_URL: 'cpp_header_image',
  NO_NOTE: 'no_note',
  NO_RECURRING: 'no_recurring',
  NO_SHIPPING: 'no_shipping',
  PROGRAMNAMES: 'program_names',
  RECURRING_OPTION: 'recurring_option',
  RETURN: 'return',
  SUBTYPE: 'sub_type',
  TYPE: 'type',
  VERSION: 'version',
  WEBSITE_CODE: 'website_code',
  BILL_FREQ: 'billing_frequency',
  DRAFT_VERSION: 'draft_version',
  DRAFT_STEP: 'draft_step',
  STATUS: 'status',
};

// All supported client button props
export const CLIENT_BUTTON = {
  ACCOUNT_IDENTIFIER_TYPE: 'accountIdentifierType',
  AMOUNT_TYPE: 'amountType',
  AMOUNT: 'amountValue',
  BN: 'bn',
  BTN_CODE_TYPE: 'useFullPageExperience',
  BTN_SIZE: 'buttonSize',
  BUSINESS: 'business',
  BUSINESS_PROFILE_LOGO_URL: 'businessProfileLogoUrl',
  BUTTON_IMAGE_URL: 'buttonImageUrl',
  BUTTON_IMAGE: 'buttonImage',
  BUTTON_NAME: 'buttonName',
  BUTTON_VARIABLES: 'extraVariablesView',
  CANCEL_RETURN: 'cancelReturn',
  CODE: 'code',
  COUNTRY_CODE: 'countryCode',
  COVER_FEES_OPTION: 'coverFeesEnabled',
  CURRENCY_CODE: 'currencyCode',
  FLOW_TYPE: 'flowType',
  ID: 'id',
  IMAGE_BANNER: 'bannerImageUrl',
  ITEM_NAME: 'purpose',
  ITEM_NUMBER: 'itemNumber',
  LANGUAGE: 'language',
  LC: 'lc',
  LISTPROGRAMS: 'listPrograms',
  LOGO_TARGET: 'logoTarget',
  LOGO_URL: 'logoUrl',
  MERCHANT_AMOUNT_1: 'merchantAmount1',
  MERCHANT_AMOUNT_2: 'merchantAmount2',
  MERCHANT_AMOUNT_3: 'merchantAmount3',
  NO_ADVANCED_VARIABLES: 'noAdvancedVariables',
  NO_BANNER_IMAGE: 'noBannerImage',
  NO_CANCEL_RETURN: 'noCancelReturn',
  NO_NOTE: 'noNote',
  NO_SHIPPING: 'noShipping',
  NOTE_LABEL: 'noteLabel',
  ORGANIZATION_NAME: 'organizationName',
  PROGRAMINPUT: 'programInput',
  PROGRAMNAMES: 'programNames',
  PURPOSE: 'purpose',
  RECEIVER_CURRENCY: 'receiverCurrency',
  RECURRING_OPTION: 'recurringEnabled',
  FREQUENCIES: 'frequencies',
  RETURN: 'return',
  NO_LOGO_URL: 'noLogoImage',
  PAYMENT_SETTINGS_LOGO_URL: 'paymentSettingsLogoUrl',
  SHOW_ACCEPTED_CARD_LOGOS: 'showAcceptedCardLogos',
  TYPE: 'type',
  USE_PAYPAL_BUTTON_STYLE: 'usePaypalButtonStyle',
  VERSION: 'version',
  DRAFT_VERSION: 'draftVersion',
  DRAFT_STEP: 'draftStep',
  STATUS: 'status',
};

/**
 * Gets value of a button property in the button_variables array
 * @param  {Object} button   Button object(server format)
 * @param  {String} property Property to get
 * @return {String}          Value of the property
 */
export function getButtonVariable(button, property) {
  const buttonVar = _find(button.button_variables, (buttonVariable) => {
    return buttonVariable.name === property;
  });

  return _get(buttonVar, 'value');
}

/**
 * Adds button variable to the button_variables array
 * @param {Object} clientFormat Button object(client format)
 * @param {String} clientKey    Client property
 * @param {Object} serverFormat Button object(server format)
 * @param {String} serverKey    Server property
 */
function addButtonVariable(clientFormat, clientKey, serverFormat, serverKey) {
  if (clientFormat[clientKey] !== undefined) {
    serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
      name: serverKey,
      value: clientFormat[clientKey],
    });
  }
}

/**
 * Returns true if accepted card logos should be displayed
 */
export const shouldShowAcceptedCardLogos = (button) => {
  return !!/CC_/.test(button.button_image_url); // eslint-disable-line
};

const buttonSizeMap = {
  sm: 'small',
  md: 'medium',
  lg: 'large',
};

/**
 * Gets button size
 * @param  {Object} button Button
 * @return {String}        Size of the button ('small' or 'large')
 */
export const getButtonSize = (button) => {
  const size = /_(\D{2})\.gif/.exec(button.button_image_url); // eslint-disable-line
  if (size && size.length) {
    return buttonSizeMap[size[1].toLowerCase()];
  }

  // Default to 'small' ?
  return 'small';
};

/**
 * Gets button image and button image url
 */
export const getButtonImageAndUrl = (
  buttonSize,
  showAcceptedCardLogos,
  locale,
  country
) => {
  const buttonImageUrl = getDonateButtonImageUrl(
    buttonSize,
    showAcceptedCardLogos,
    locale,
    country
  );
  let buttonImage = 'REG';
  if (showAcceptedCardLogos) {
    buttonImage = 'CC';
  }

  if (buttonSize === 'small') {
    buttonImage = 'SML';
  }

  return {
    button_image: buttonImage,
    button_image_url: buttonImageUrl,
  };
};

/**
 * Returns true if its a Paypal button
 */
export const shouldUsePayPalButtonStyle = (button) => {
  const buttonImageUrl = button.button_image_url || '';
  if (buttonImageUrl.length) {
    if (
      buttonImageUrl.includes('btn_donate') &&
      buttonImageUrl.includes(templateData.cdnHostName)
    ) {
      return 'yes';
    }
  }

  return 'no';
};

export const getAccountIdentifierType = (value) => {
  // Is this an email address or encrypted_account_number
  if (value && value.indexOf('@') !== -1) {
    return ACCOUNT_IDENTIFIER_TYPES.EMAIL_ADDRESS;
  }
  return ACCOUNT_IDENTIFIER_TYPES.MERCHANT_ACCOUNT_ID;
};

export const getAccountIdentifierValue = (isServerToClient, serverFormat) => {
  const isLoggedIn = isLoggedInUser({ authState: getServerProps().authState });
  const accountIdentifier = getServerProps().accountIdentifier;

  if (!isLoggedIn) {
    return getButtonVariable(serverFormat, SERVER_BUTTON.BUSINESS);
  }

  if (isServerToClient) {
    return getButtonVariable(serverFormat, SERVER_BUTTON.BUSINESS);
  }

  return _get(accountIdentifier, 'encrypted_account_number');
};

export const getLogoUrlFieldValues = (serverFormatButton) => {
  const businessProfileLogoUrl = serverFormatButton?.businessImageUrls?.url;
  let paymentSettingsLogoUrl = '';

  // Set logo values for clientSide
  const logoUrl = getButtonVariable(serverFormatButton, SERVER_BUTTON.LOGO_URL);

  let logoTarget = getButtonVariable(
    serverFormatButton,
    SERVER_BUTTON.LOGO_TARGET
  );

  const version = getButtonVariable(serverFormatButton, SERVER_BUTTON.VERSION);
  let noLogoImage = 'yes';
  switch (logoTarget) {
    case LOGO_TARGET_PROFILE:
      if (_isEmpty(version)) {
        paymentSettingsLogoUrl =
          serverFormatButton?.merchantExperienceUrls?.logoUrl;
      }
      noLogoImage = 'no';
      break;
    case LOGO_TARGET_BUTTON:
      noLogoImage = 'no';
      break;
    case LOGO_TARGET_NOLOGO:
      noLogoImage = 'yes';
      break;
    default:
      if (!_isEmpty(logoUrl)) {
        logoTarget = LOGO_TARGET_BUTTON;
      } else {
        logoTarget = LOGO_TARGET_PROFILE;
      }
      noLogoImage = 'no';

      break;
  }

  return {
    logoTarget,
    logoUrl,
    businessProfileLogoUrl,
    paymentSettingsLogoUrl,
    noLogoImage,
  };
};

export const checkIfOldButton = (serverFormat) => {
  return _isEmpty(serverFormat[SERVER_BUTTON.DONATE_CONTEXT]);
};

export const getRecurringOption = (serverFormat) => {
  const isOldButton = checkIfOldButton(serverFormat);
  const noRecurring = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.NO_RECURRING
  );

  if (isOldButton && _isEmpty(noRecurring)) {
    const amountValue = getButtonVariable(serverFormat, SERVER_BUTTON.AMOUNT);
    // for button with fixed amount, default to false.
    return amountValue > 0 ? 'false' : 'true';
  }

  if (noRecurring === '0') {
    return 'true';
  } else if (noRecurring === '1') {
    return 'false';
  }

  return serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
    SERVER_BUTTON.RECURRING_OPTION
  ];
};

export const getSupportedRecurringFrequencies = (serverFormat) => {
  if (
    !serverFormat[SERVER_BUTTON.DONATE_CONTEXT] ||
    (serverFormat[SERVER_BUTTON.DONATE_CONTEXT] &&
      !serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
        SERVER_BUTTON.RECURRING_EXTN_DATA
      ])
  ) {
    return ['MONTH'];
  }
  return JSON.parse(
    serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
      SERVER_BUTTON.RECURRING_EXTN_DATA
    ]
  )[SERVER_BUTTON.BILL_FREQ];
};

/**
 * Transform button from server format to client format
 */
export const transformServerFormatToClient = (serverFormat) => {
  const isHosted = _includes(
    ['BUSINESS', 'PREMIER'],
    getServerProps().userType
  );

  const clientFormat = {};

  if (serverFormat.id) {
    clientFormat.id = serverFormat.id;
  }

  // Top level variables
  clientFormat[CLIENT_BUTTON.BUTTON_IMAGE] =
    serverFormat[SERVER_BUTTON.BUTTON_IMAGE];
  clientFormat[CLIENT_BUTTON.BUTTON_IMAGE_URL] =
    serverFormat[SERVER_BUTTON.BUTTON_IMAGE_URL];
  clientFormat[CLIENT_BUTTON.STATUS] = serverFormat[SERVER_BUTTON.STATUS];

  // variables from the button_variables array
  clientFormat[CLIENT_BUTTON.DRAFT_VERSION] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.DRAFT_VERSION
  );
  clientFormat[CLIENT_BUTTON.DRAFT_STEP] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.DRAFT_STEP
  );

  const noShipping = getButtonVariable(serverFormat, SERVER_BUTTON.NO_SHIPPING);
  clientFormat[CLIENT_BUTTON.NO_SHIPPING] = noShipping === '0' ? 'no' : 'yes';

  const noNote = getButtonVariable(serverFormat, SERVER_BUTTON.NO_NOTE);
  clientFormat[CLIENT_BUTTON.NO_NOTE] = noNote === '0' ? 'no' : 'yes';
  clientFormat[CLIENT_BUTTON.NOTE_LABEL] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.CN
  );

  const useFullPageExperience = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.BTN_CODE_TYPE
  );
  clientFormat[CLIENT_BUTTON.BTN_CODE_TYPE] =
    useFullPageExperience === 'S' ? 'no' : 'yes';
  const bannerImageUrl = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.IMAGE_BANNER
  );
  clientFormat[CLIENT_BUTTON.NO_BANNER_IMAGE] = !_isEmpty(bannerImageUrl)
    ? 'no'
    : 'yes';
  clientFormat[CLIENT_BUTTON.IMAGE_BANNER] = bannerImageUrl;

  clientFormat[CLIENT_BUTTON.RECEIVER_CURRENCY] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.CURRENCY_CODE
  )
    ? getButtonVariable(serverFormat, SERVER_BUTTON.CURRENCY_CODE)
    : getServerProps().primaryCurrency[0];
  clientFormat[CLIENT_BUTTON.CANCEL_RETURN] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.CANCEL_RETURN
  );
  clientFormat[CLIENT_BUTTON.RETURN] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.RETURN
  );
  clientFormat[CLIENT_BUTTON.NO_CANCEL_RETURN] =
    !isEmpty(clientFormat[CLIENT_BUTTON.CANCEL_RETURN]) ||
    !isEmpty(clientFormat[CLIENT_BUTTON.RETURN])
      ? 'no'
      : 'yes';
  clientFormat[CLIENT_BUTTON.PURPOSE] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.ITEM_NAME
  );
  clientFormat[CLIENT_BUTTON.ITEM_NUMBER] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.ITEM_NUMBER
  );
  clientFormat[CLIENT_BUTTON.COVER_FEES_OPTION] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.COVER_FEES_OPTION
  );
  clientFormat[CLIENT_BUTTON.BUTTON_NAME] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.BUTTON_NAME
  );
  clientFormat[CLIENT_BUTTON.FLOW_TYPE] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.FLOW_TYPE
  );
  clientFormat[CLIENT_BUTTON.ACCOUNT_IDENTIFIER_TYPE] =
    getAccountIdentifierType(
      getButtonVariable(serverFormat, SERVER_BUTTON.BUSINESS)
    );
  clientFormat[CLIENT_BUTTON.BUSINESS] = getAccountIdentifierValue(
    true,
    serverFormat
  );

  const countryCode = serverFormat[SERVER_BUTTON.COUNTRY_CODE];
  clientFormat[CLIENT_BUTTON.COUNTRY_CODE] = countryCode;
  clientFormat[CLIENT_BUTTON.LANGUAGE] = serverFormat[SERVER_BUTTON.LANGUAGE];

  // Derived variables
  clientFormat[CLIENT_BUTTON.USE_PAYPAL_BUTTON_STYLE] =
    shouldUsePayPalButtonStyle(serverFormat);
  clientFormat[CLIENT_BUTTON.SHOW_ACCEPTED_CARD_LOGOS] =
    shouldShowAcceptedCardLogos(serverFormat);
  clientFormat[CLIENT_BUTTON.BTN_SIZE] = getButtonSize(serverFormat);

  const logoFieldValues = getLogoUrlFieldValues(serverFormat);
  clientFormat[CLIENT_BUTTON.LOGO_URL] = logoFieldValues.logoUrl;
  clientFormat[CLIENT_BUTTON.LOGO_TARGET] =
    getButtonVariable(serverFormat, SERVER_BUTTON.LOGO_TARGET) ||
    logoFieldValues.logoTarget;
  clientFormat[CLIENT_BUTTON.PAYMENT_SETTINGS_LOGO_URL] =
    logoFieldValues.paymentSettingsLogoUrl;
  clientFormat[CLIENT_BUTTON.BUSINESS_PROFILE_LOGO_URL] =
    logoFieldValues.businessProfileLogoUrl;
  clientFormat[CLIENT_BUTTON.NO_LOGO_URL] = logoFieldValues.noLogoImage;

  const supportedVariables = getSupportedVariables(
    serverFormat.button_variables
  );
  clientFormat.extraVariablesView =
    formatVariablesForTextArea(supportedVariables);
  clientFormat.noAdvancedVariables = isEmpty(clientFormat.extraVariablesView)
    ? 'yes'
    : 'no';

  const unsupportedVariables = getUnsupportedVariables(
    serverFormat.button_variables
  );
  clientFormat.unsupportedVariables = unsupportedVariables;

  const amount = getButtonVariable(serverFormat, SERVER_BUTTON.AMOUNT);

  if (amount !== undefined) {
    clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] = 'fixed';
    clientFormat[CLIENT_BUTTON.AMOUNT] = formatNumber(Number(amount));
  } else {
    clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] = 'variable';
  }

  const coverFees = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.COVER_FEES_OPTION
  );
  clientFormat[CLIENT_BUTTON.COVER_FEES_OPTION] =
    coverFees === 'true' ? 'yes' : 'no';
  const recurringOption = getRecurringOption(serverFormat);
  clientFormat[CLIENT_BUTTON.RECURRING_OPTION] =
    recurringOption === 'true' ? 'yes' : 'no';
  if (isHosted) {
    if (recurringOption === 'true') {
      const supportedRecurringFrequencies =
        getSupportedRecurringFrequencies(serverFormat);

      clientFormat[CLIENT_BUTTON.FREQUENCIES] = [];
      if (supportedRecurringFrequencies.includes('WEEK')) {
        clientFormat[CLIENT_BUTTON.FREQUENCIES].push('Weekly');
      }
      if (supportedRecurringFrequencies.includes('MONTH')) {
        clientFormat[CLIENT_BUTTON.FREQUENCIES].push('Monthly');
      }
      if (supportedRecurringFrequencies.includes('YEAR')) {
        clientFormat[CLIENT_BUTTON.FREQUENCIES].push('Yearly');
      }
    } else {
      clientFormat[CLIENT_BUTTON.FREQUENCIES] = ['Monthly', 'Yearly'];
    }
  } else {
    clientFormat[CLIENT_BUTTON.FREQUENCIES] = ['Monthly', 'Yearly'];
  }

  if (!checkIfOldButton(serverFormat)) {
    // For 'fixed' amounttype, do not read from 'donate_context'
    if (clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] !== 'fixed') {
      let amountType =
        serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.AMOUNT_TYPE];
      if (!amountType) {
        amountType = 'variable';
      }
      amountType = amountType.toLowerCase();
      clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] = amountType;

      if (clientFormat[CLIENT_BUTTON.AMOUNT_TYPE] === 'merchant_recommended') {
        const merchAmount1 = _get(
          serverFormat[SERVER_BUTTON.DONATE_CONTEXT],
          `${SERVER_BUTTON.AMOUNT_OPTIONS}[0]`
        );
        const merchAmount2 = _get(
          serverFormat[SERVER_BUTTON.DONATE_CONTEXT],
          `${SERVER_BUTTON.AMOUNT_OPTIONS}[1]`
        );
        const merchAmount3 = _get(
          serverFormat[SERVER_BUTTON.DONATE_CONTEXT],
          `${SERVER_BUTTON.AMOUNT_OPTIONS}[2]`
        );
        clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_1] = formatNumber(
          Number(merchAmount1) || 0
        );
        clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_2] = formatNumber(
          Number(merchAmount2) || 0
        );
        clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_3] = formatNumber(
          Number(merchAmount3) || 0
        );
      }
    }

    const programNames =
      serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.PROGRAMNAMES];
    if (!_isEmpty(programNames)) {
      clientFormat[CLIENT_BUTTON.LISTPROGRAMS] = 'yes';
      clientFormat[CLIENT_BUTTON.PROGRAMNAMES] = programNames;

      for (let i = 0; i < programNames.length; i++) {
        clientFormat[`${CLIENT_BUTTON.PROGRAMINPUT}${i}`] = programNames[i];
      }
    } else {
      clientFormat[CLIENT_BUTTON.LISTPROGRAMS] = 'no';
      clientFormat[CLIENT_BUTTON.PROGRAMNAMES] = [];
    }
  }

  // case: 'NEW PROGRAMS'
  // If it is V1 button, convert V1Program to V2Program
  // If it is V2 button, nothing to convert
  if (checkIfOldButton(serverFormat)) {
    const v0Program = getButtonVariable(
      serverFormat,
      SERVER_BUTTON.ITEM_NUMBER
    );
    if (v0Program) {
      clientFormat[CLIENT_BUTTON.LISTPROGRAMS] = 'yes';
      clientFormat[CLIENT_BUTTON.PROGRAMNAMES] = [v0Program];
    } else {
      clientFormat[CLIENT_BUTTON.LISTPROGRAMS] = 'no';
      clientFormat[CLIENT_BUTTON.PROGRAMNAMES] = [];
    }
  }

  // version
  clientFormat[CLIENT_BUTTON.VERSION] = getButtonVariable(
    serverFormat,
    SERVER_BUTTON.VERSION
  );

  return clientFormat;
};

/**
 * Transform button from client format to server format
 */
export const transformClientFormatToServer = (clientFormat) => {
  const serverFormat = {};

  if (clientFormat.id) {
    serverFormat[SERVER_BUTTON.ID] = clientFormat.id;
  }

  const isLoggedIn = isLoggedInUser({ authState: getServerProps().authState });
  const isHosted = _includes(
    ['BUSINESS', 'PREMIER'],
    getServerProps().userType
  );
  const isMerchantIdEnabled = _includes(
    ['BUSINESS', 'PREMIER', 'PERSONAL'],
    getServerProps().userType
  );
  const isProgramEnabled = getServerProps().enableProgram;
  const isMerchantPresetAmountEnabled =
    getServerProps().enableMerchantPresetAmount;
  const isRecurringEnabled = getServerProps().enableRecurring;
  serverFormat[SERVER_BUTTON.COUNTRY_CODE] = clientFormat.countryCode;
  serverFormat[SERVER_BUTTON.TYPE] = 'DONATE';
  serverFormat[SERVER_BUTTON.SUBTYPE] = 'PRODUCTS';
  serverFormat[SERVER_BUTTON.LANGUAGE] = clientFormat.language;

  // Create array of `button_variables`
  serverFormat[SERVER_BUTTON.BUTTON_VARIABLES] = [];

  // btn_code_type
  const useFullPageExperience = clientFormat[CLIENT_BUTTON.BTN_CODE_TYPE];
  serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
    name: SERVER_BUTTON.BTN_CODE_TYPE,
    value: useFullPageExperience === 'no' ? 'S' : 'H',
  });

  addButtonVariable(
    clientFormat,
    CLIENT_BUTTON.BUTTON_NAME,
    serverFormat,
    SERVER_BUTTON.BUTTON_NAME
  );

  // send the version of button creation
  addButtonVariable(
    clientFormat,
    CLIENT_BUTTON.VERSION,
    serverFormat,
    SERVER_BUTTON.VERSION
  );

  addButtonVariable(
    clientFormat,
    CLIENT_BUTTON.PURPOSE,
    serverFormat,
    SERVER_BUTTON.ITEM_NAME
  );

  addButtonVariable(
    clientFormat,
    CLIENT_BUTTON.FLOW_TYPE,
    serverFormat,
    SERVER_BUTTON.FLOW_TYPE
  );

  const noCancelReturn = clientFormat.noCancelReturn === 'yes';
  if (!noCancelReturn) {
    addButtonVariable(
      clientFormat,
      CLIENT_BUTTON.CANCEL_RETURN,
      serverFormat,
      SERVER_BUTTON.CANCEL_RETURN
    );
    addButtonVariable(
      clientFormat,
      CLIENT_BUTTON.RETURN,
      serverFormat,
      SERVER_BUTTON.RETURN
    );
  }

  addButtonVariable(
    clientFormat,
    CLIENT_BUTTON.RECEIVER_CURRENCY,
    serverFormat,
    SERVER_BUTTON.CURRENCY_CODE
  );

  // Adding draft variables
  addButtonVariable(
    clientFormat,
    CLIENT_BUTTON.DRAFT_VERSION,
    serverFormat,
    SERVER_BUTTON.DRAFT_VERSION
  );

  addButtonVariable(
    clientFormat,
    CLIENT_BUTTON.DRAFT_STEP,
    serverFormat,
    SERVER_BUTTON.DRAFT_STEP
  );

  if (!isHosted && isRecurringEnabled) {
    serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
      name: SERVER_BUTTON.NO_RECURRING,
      value: clientFormat[CLIENT_BUTTON.RECURRING_OPTION] === 'yes' ? 0 : 1,
    });
  }

  const noLogo = clientFormat.noLogoImage;
  if (isLoggedIn) {
    if (noLogo === 'no') {
      addButtonVariable(
        clientFormat,
        CLIENT_BUTTON.LOGO_TARGET,
        serverFormat,
        SERVER_BUTTON.LOGO_TARGET
      );
    } else {
      serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
        name: SERVER_BUTTON.LOGO_TARGET,
        value: LOGO_TARGET_NOLOGO,
      });
    }
  }

  if (noLogo === 'no') {
    if (
      clientFormat.logoTarget &&
      clientFormat.logoTarget === LOGO_TARGET_BUTTON
    ) {
      if (clientFormat.logoUrl) {
        serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
          name: SERVER_BUTTON.LOGO_URL,
          value: clientFormat.logoUrl,
        });
      }
    }
  }

  // Business field
  if (isLoggedIn && isMerchantIdEnabled) {
    const accountIdentifierValue = getAccountIdentifierValue(false);
    if (accountIdentifierValue) {
      serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
        name: SERVER_BUTTON.BUSINESS,
        value: accountIdentifierValue,
      });
    }
  } else {
    clientFormat[CLIENT_BUTTON.BUSINESS] = (
      clientFormat[CLIENT_BUTTON.BUSINESS] || ''
    ).toLowerCase();
    addButtonVariable(
      clientFormat,
      CLIENT_BUTTON.BUSINESS,
      serverFormat,
      SERVER_BUTTON.BUSINESS
    );
  }

  // Amount field
  const amountType = clientFormat[CLIENT_BUTTON.AMOUNT_TYPE];
  if (amountType === 'fixed' && clientFormat[CLIENT_BUTTON.AMOUNT]) {
    serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
      name: SERVER_BUTTON.AMOUNT,
      value: normalizeNumber(clientFormat[CLIENT_BUTTON.AMOUNT]),
    });
  }

  if (isHosted) {
    // Generate donate_context
    // Set `amount_type`
    if (
      isMerchantPresetAmountEnabled ||
      isRecurringEnabled ||
      isProgramEnabled
    ) {
      serverFormat[SERVER_BUTTON.DONATE_CONTEXT] = {};
    }

    // Create array of `amount_options`
    if (isMerchantPresetAmountEnabled && amountType !== 'fixed') {
      serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.AMOUNT_TYPE] =
        amountType.toUpperCase();

      if (amountType === 'merchant_recommended') {
        serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
          SERVER_BUTTON.AMOUNT_OPTIONS
        ] = [];
        serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
          SERVER_BUTTON.AMOUNT_OPTIONS
        ].push(
          normalizeNumber(clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_1] || 0)
        );
        serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
          SERVER_BUTTON.AMOUNT_OPTIONS
        ].push(
          normalizeNumber(clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_2] || 0)
        );
        serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
          SERVER_BUTTON.AMOUNT_OPTIONS
        ].push(
          normalizeNumber(clientFormat[CLIENT_BUTTON.MERCHANT_AMOUNT_3] || 0)
        );

        if (clientFormat[SERVER_BUTTON.STATUS] === 'D') {
          if (
            serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
              SERVER_BUTTON.AMOUNT_OPTIONS
            ].every((e) => e === '' || e === 0)
          ) {
            serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
              SERVER_BUTTON.AMOUNT_OPTIONS
            ] = null;
          }
        }
      }
    }

    // Recurring option
    if (isRecurringEnabled) {
      serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
        SERVER_BUTTON.RECURRING_OPTION
      ] =
        clientFormat[CLIENT_BUTTON.RECURRING_OPTION] === 'yes'
          ? 'true'
          : 'false';
    }

    if (isHosted && isRecurringEnabled) {
      if (clientFormat[CLIENT_BUTTON.RECURRING_OPTION] === 'yes') {
        const billingFrequencies = ['ONE_TIME'];
        if (clientFormat[CLIENT_BUTTON.FREQUENCIES].includes('Weekly')) {
          billingFrequencies.push('WEEK');
        }
        if (clientFormat[CLIENT_BUTTON.FREQUENCIES].includes('Monthly')) {
          billingFrequencies.push('MONTH');
        }
        if (clientFormat[CLIENT_BUTTON.FREQUENCIES].includes('Yearly')) {
          billingFrequencies.push('YEAR');
        }
        serverFormat[SERVER_BUTTON.DONATE_CONTEXT][
          SERVER_BUTTON.RECURRING_EXTN_DATA
        ] = JSON.stringify({ billing_frequency: billingFrequencies });
      }
    }

    // Program Names
    if (isProgramEnabled) {
      if (clientFormat[CLIENT_BUTTON.LISTPROGRAMS] === 'yes') {
        serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.PROGRAMNAMES] =
          clientFormat[CLIENT_BUTTON.PROGRAMNAMES];
      } else {
        serverFormat[SERVER_BUTTON.DONATE_CONTEXT][SERVER_BUTTON.PROGRAMNAMES] =
          [];
      }
    }
  }

  // Handle ITEM_NUMBER
  // When editing a V1 button in V2, ITEM_NUMBER has to be set to ""
  if (isHosted) {
    if (isProgramEnabled) {
      serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
        name: SERVER_BUTTON.ITEM_NUMBER,
        value: '',
      });
    } else {
      addButtonVariable(
        clientFormat,
        CLIENT_BUTTON.ITEM_NUMBER,
        serverFormat,
        SERVER_BUTTON.ITEM_NUMBER
      );
    }
  } else {
    addButtonVariable(
      clientFormat,
      CLIENT_BUTTON.ITEM_NUMBER,
      serverFormat,
      SERVER_BUTTON.ITEM_NUMBER
    );
  }

  // no_shipping field
  if (isHosted) {
    const noShipping = clientFormat[CLIENT_BUTTON.NO_SHIPPING];
    serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
      name: SERVER_BUTTON.NO_SHIPPING,
      value: noShipping === 'no' ? 0 : 1,
    });
  } else {
    serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
      name: SERVER_BUTTON.NO_SHIPPING,
      value: 1,
    });
  }

  if (isHosted) {
    const coverFees = clientFormat[CLIENT_BUTTON.COVER_FEES_OPTION];
    serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
      name: SERVER_BUTTON.COVER_FEES_OPTION,
      value: coverFees === 'yes' ? 'true' : 'false',
    });
  }

  // no_note field
  const noNote = clientFormat[CLIENT_BUTTON.NO_NOTE];
  serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
    name: SERVER_BUTTON.NO_NOTE,
    value: noNote === 'no' ? 0 : 1,
  });

  // custom note label
  if (noNote === 'no') {
    serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
      name: SERVER_BUTTON.CN,
      value: clientFormat[CLIENT_BUTTON.NOTE_LABEL],
    });
  }

  // Add button_image and button_image_url
  if (clientFormat.usePaypalButtonStyle === 'no') {
    serverFormat.button_image_url = clientFormat.buttonImageUrl;
  } else {
    const buttonImageProps = getButtonImageAndUrl(
      clientFormat.buttonSize,
      clientFormat.showAcceptedCardLogos,
      clientFormat.language,
      clientFormat.countryCode
    );
    serverFormat.button_image_url = buttonImageProps.button_image_url;
    serverFormat.button_image = buttonImageProps.button_image;
  }

  const noBannerImage = clientFormat.noBannerImage;
  if (noBannerImage === 'no') {
    serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push({
      name: SERVER_BUTTON.IMAGE_BANNER,
      value: clientFormat.bannerImageUrl,
    });
  }

  const noAdvancedVariables = clientFormat.noAdvancedVariables === 'yes';
  if (!noAdvancedVariables && clientFormat.extraVariablesView) {
    const extraVariables = parseTextAreaValue(clientFormat.extraVariablesView);
    _each(extraVariables, (variable) => {
      serverFormat[SERVER_BUTTON.BUTTON_VARIABLES].push(variable);
    });
  }

  return serverFormat;
};

export function mapServerFieldToClientField(server_field) {
  const field = _findKey(SERVER_BUTTON, (item) => item === server_field);
  return CLIENT_BUTTON[field];
}
